import { useMutation, useQueryClient } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import { invalidateIfNotInFlight } from '@/patients/lib/invalidate-if-not-in-flight';
import type { ConsentTemplateBody } from '@/consent/types/consent-template-api';

/**
 * POST /api/ConsentTemplate/CreateUpdateTemplate → string (template GUID).
 *
 * Contract: Scrips.Patient `ConsentTemplateController.CreateUpdateTemplate` /
 * `ConsentTemplateRequest` (PR #882). `id == null` creates a new draft;
 * `id` set updates in place. `versionHash` is computed server-side from the
 * bilingual body — the client never sends it.
 *
 * No date fields on the body, so no no-Z handling needed.
 */
export function useSaveConsentTemplate() {
  const qc = useQueryClient();
  return useMutation<string, Error, ConsentTemplateBody>({
    // LOAD-BEARING (Samer review #550): do NOT add `versionHash` to this body.
    // It is computed server-side (SHA-256 of the bilingual body) to pin a signed
    // consent to the exact revision shown. A client-sent hash would let the wire
    // claim a revision the body doesn't match — a re-consent integrity hole.
    mutationFn: (body) =>
      post<string>('/api/ConsentTemplate/CreateUpdateTemplate', body),
    onSuccess: (_id, body) => {
      invalidateIfNotInFlight(qc, ['consent-templates']);
      if (body.id) invalidateIfNotInFlight(qc, ['consent-template', body.id]);
    },
  });
}
